import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectColorFinderFlightById } from "features/entity-caching/flights/selectors"
import { ColorFinderTimeline } from "./ColorFinderTimeline"
import {
    ColorFinderFootageDisplay,
    FootageClickHandler,
    GetMediaControls,
    MediaControls,
    MediaListener,
} from "./ColorFinderFootageDisplay"
import { IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useComments } from "features/comments/Comments"
import {
    ColorFinderCommentInput,
    ColorFinderCommentRepliesFeed,
    ColorFinderFeed,
} from "./ColorFinderFeed"
import { ImageColorFinderFlight, VideoColorFinderFlight } from "@nova/protobuf"
import {
    isVideoFrameMetadataGeoValid,
    useColorFinderMetadata,
    useColorTiler,
} from "./ColorFinderQueries"
import { useCumulativeRGB } from "./CumulativeRGB"
import classNames from "classnames"
import { getVideoFrameBboxAsync } from "features/map/layers/video/VideoProjectionLayer"
import { createIcon } from "features/pubsub/entityActions/cubes/icon"
import { getIconUrl } from "@nova/component-map-icons"
import { selectUserID } from "user/profile/selectors"
import {
    setColorFinderFeedFilters,
    setColorFinderMapIsSynced,
    setColorFinderTool,
    setColorFinderView,
} from "./redux/slice"
import { IconPackOption, LineWidth } from "@nova/constants"
import { incrementNumberStartAt, selectIconTemplate } from "plugins/Toolbox/tool-registry"
import { defaultIconColor } from "features/map/layers/icons/constants"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { Popup } from "@nova/component-popup"
import { Checkbox } from "@nova/component-checkbox"
import { selectIconsByTargetId } from "features/entity-caching/icons/selectors"
import { ColorFinderIconTargetPayload } from "plugins/IconTargetPayload"
import { ColorFinderLeftPanel } from "./ColorFinderLeftPanel"
import { useColorSampleState } from "./useColorSampleState"
import {
    selectColorFinderFeedFilters,
    selectColorFinderAnomalyEnabled,
    selectColorFinderFlightId,
    selectColorFinderSourceEnabled,
    selectColorFinderTool,
    selectColorFinderView,
    selectColorFinderMapIsSynced,
    selectColorFinderAnomalyOpacity,
    selectColorFinderAnomalySensitivity,
} from "./redux/selectors"
import {
    useLayoutEffect,
    forwardRef,
    FunctionComponent,
    ReactNode,
    useMemo,
    useRef,
    useState,
    useEffect,
    useCallback,
    MouseEventHandler,
    PropsWithChildren,
} from "react"
import { ColorFinderTiler } from "./ColorFinderTiler.class"
import { Comment } from "@nova/schemas"
import { useAnomalyIndex } from "./anomalies/useAnomalyIndex"
import { Scrubbable } from "./Scrubbable"
import { setNewViewstate } from "features/map/GlobalViewState"
import { setFollowingEntity } from "plugins/shared/playback/state/slice"
import { useMapBackground } from "features/map/control/useMapStyles"
import { subscribeToFootageEvent } from "./ColorFinderEvents"

const useReparenting = (
    vaultRef: React.RefObject<HTMLDivElement | null>,
    targetRef: HTMLElement | null
) => {
    useLayoutEffect(() => {
        const content = vaultRef.current

        const vault = content?.parentElement

        if (!content || !targetRef || !vault) return

        targetRef.appendChild(content)

        content.style.width = "100%"
        content.style.height = "100%"
        window.dispatchEvent(new Event("resize"))

        return () => {
            if (vault.isConnected) {
                vault.appendChild(content)
            }
        }
    }, [vaultRef, targetRef])
}

export type RGB = [number, number, number]
export type ColorFilter = Set<number>

interface ColorFinderPanelProps {
    map: ReactNode
}

const ColorFinderPanel: FunctionComponent<ColorFinderPanelProps> = ({ map }) => {
    const dispatch = useAppDispatch()
    const flightId = useAppSelector(selectColorFinderFlightId)
    const flight = useAppSelector(selectColorFinderFlightById(flightId))
    const isMapSynced = useAppSelector(selectColorFinderMapIsSynced)

    const { data: tiler } = useColorTiler(flight)
    const { data: anomalyIndex } = useAnomalyIndex(flight)
    const { data: metadata } = useColorFinderMetadata(flight)
    const view = useAppSelector(selectColorFinderView)
    const [drawnFilter, setDrawnFilter] = useState<ColorFilter>(() => new Set())
    const { addSample, samples, removeSample } = useColorSampleState()

    const filter = useMemo(() => {
        return samples.reduce((prev: ColorFilter, curr) => {
            return prev.union(curr.filter)
        }, drawnFilter)
    }, [drawnFilter, samples])

    const videoControls = useRef<MediaControls>(null)
    const { data: cumulativeRgb } = useCumulativeRGB(flight)
    const { comments = [] } = useComments<"cf-frame">({ entityId: flight.id })
    const sortedComments = comments.sort((a, b) => a.context.frame - b.context.frame)

    const [leftSlot, setLeftSlot] = useState<HTMLElement | null>(null)
    const [centerSlot, setCenterSlot] = useState<HTMLElement | null>(null)
    const [bottomSlot, setBottomSlot] = useState<HTMLElement | null>(null)

    const mapVaultRef = useRef<HTMLDivElement>(null)
    const footageVaultRef = useRef<HTMLDivElement>(null)
    const timelineVaultRef = useRef<HTMLDivElement>(null)

    let footageTarget = centerSlot
    let mapTarget = leftSlot
    const timelineTarget = bottomSlot

    if (view === "flipped") {
        footageTarget = leftSlot
        mapTarget = centerSlot
    } else if (view === "fullscreen") {
        footageTarget = centerSlot
        mapTarget = leftSlot
    }

    useReparenting(mapVaultRef, mapTarget)
    useReparenting(footageVaultRef, footageTarget)
    useReparenting(timelineVaultRef, timelineTarget)

    const centerMapOnFrame = useCallback(
        (frame: number) => {
            const controls = videoControls.current
            if (!metadata || !controls) return

            const frameMetadata = metadata[frame]
            if (!frameMetadata) return

            if (!isVideoFrameMetadataGeoValid(frameMetadata)) return

            dispatch(setFollowingEntity(undefined))
            setNewViewstate({
                longitude: frameMetadata[0],
                latitude: frameMetadata[1],
                bearing: frameMetadata[4],
            })
        },
        [dispatch, metadata]
    )

    useEffect(() => {
        const mediaListener: MediaListener = (state) => {
            /* This timeout solves the view state sometimes not being set to the first frame of the colorfinder footage.
            My guess is that the map doesn't like being asked to re-size + have view state change at the same time. */
            if (isMapSynced) setTimeout(() => centerMapOnFrame(state.frame), 250)
        }

        const unsubscribe = subscribeToFootageEvent(mediaListener)

        return () => {
            unsubscribe()
        }
    }, [centerMapOnFrame, isMapSynced])

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const mediaState = videoControls.current?.getState()

            switch (e.key) {
                case " ":
                    if (mediaState?.isPlaying) {
                        videoControls.current?.pause()
                    } else {
                        videoControls.current?.play()
                    }
                    break
                case "p":
                case "P":
                    dispatch(setColorFinderTool("point"))
                    break
                case "Escape":
                    dispatch(setColorFinderView("default"))
                    dispatch(setColorFinderTool(""))
                    break
                case "ArrowLeft":
                    if (mediaState) {
                        videoControls.current?.setFrame(mediaState.frame - 1)
                    }
                    break
                case "ArrowRight":
                    if (mediaState) {
                        videoControls.current?.setFrame(mediaState.frame + 1)
                    }
                    break
            }
        }

        document.addEventListener("keydown", handler)
        return () => {
            document.removeEventListener("keydown", handler)
        }
    }, [dispatch])

    if (!flight) return null

    const handleCenterMapClick: MouseEventHandler = (e) => {
        e.stopPropagation()
        const frame = videoControls.current?.getFrame()
        if (frame !== undefined) centerMapOnFrame(frame)
    }
    const handleSyncMapClick: MouseEventHandler = (e) => {
        e.stopPropagation()
        dispatch(setColorFinderMapIsSynced(true))
        const frame = videoControls.current?.getFrame()
        if (frame !== undefined) centerMapOnFrame(frame)
    }

    return (
        <>
            {view === "fullscreen" ? (
                <div className="h-full w-full p-2 min-h-0">
                    <ColorFinderFullScreen
                        footageRef={setCenterSlot}
                        mapRef={setLeftSlot}
                        timelineRef={setBottomSlot}
                    />
                </div>
            ) : (
                <div className="h-full flex flex-col text-gray-200 gap-2 p-2 pt-0 flex-1 min-h-0">
                    <div className="flex gap-2 space-between flex-1 min-h-0">
                        {tiler && cumulativeRgb && (
                            <ColorFinderLeftPanel
                                getMediaControls={() => videoControls.current}
                                tiler={tiler}
                                onFilterChange={setDrawnFilter}
                                mapRef={setLeftSlot}
                                cumulativeRgb={cumulativeRgb}
                                flight={flight}
                                onAddSample={addSample}
                                onRemoveSample={removeSample}
                                activeSamples={samples}
                            />
                        )}
                        <div className="flex-1 min-h-0 bg-black-1000 rounded-lg">
                            <div ref={setCenterSlot} className="h-full" />
                        </div>
                        {tiler && (
                            <ColorFinderRightPanel
                                comments={sortedComments}
                                tiler={tiler}
                                getMediaControls={() => videoControls.current}
                                flight={flight}
                            />
                        )}
                    </div>
                    <div className="shrink-0" ref={setBottomSlot} />
                </div>
            )}

            <div className="hidden">
                <Scrubbable
                    onScrub={() => {
                        dispatch(setColorFinderMapIsSynced(false))
                    }}
                >
                    <div ref={mapVaultRef} className="h-full w-full relative">
                        {map}
                        {!isMapSynced && isVideoFrameMetadataGeoValid(metadata?.[0]) && (
                            <div className="absolute flex gap-1 bottom-2 right-2">
                                <IconButton
                                    onClick={handleCenterMapClick}
                                    className="bg-black-600 p-1 text-gray-50 rounded-md"
                                >
                                    <Icons.SmallIcon Icon={Icons.ZoomTo} />
                                </IconButton>

                                <IconButton
                                    onClick={handleSyncMapClick}
                                    className="bg-black-600 p-1 text-gray-50 rounded-md"
                                >
                                    <Icons.SmallIcon Icon={Icons.AssistantNavigation} />
                                </IconButton>
                            </div>
                        )}
                    </div>
                </Scrubbable>

                <div ref={footageVaultRef} className="h-full w-full">
                    {tiler && (
                        <ColorFinderFootageContainer
                            ref={videoControls}
                            colorFilter={filter}
                            flight={flight}
                            numFrames={tiler.getNumFrames()}
                        />
                    )}
                </div>

                <div ref={timelineVaultRef} className="h-full w-full">
                    {tiler && (
                        <ColorFinderTimeline
                            flight={flight}
                            comments={sortedComments}
                            getControls={() => videoControls.current}
                            filter={filter}
                            tiler={tiler}
                            anomalyIndex={anomalyIndex}
                        />
                    )}
                </div>
            </div>
        </>
    )
}
interface ColorFinderFullScreenProps {
    footageRef: (node: HTMLDivElement | null) => void
    mapRef: (node: HTMLDivElement | null) => void
    timelineRef: (node: HTMLDivElement | null) => void
}

const ColorFinderFullScreen: FunctionComponent<ColorFinderFullScreenProps> = ({
    footageRef,
    mapRef,
    timelineRef,
}) => {
    return (
        <div className="flex flex-col w-full h-full text-gray-200">
            <div className="h-full flex gap-2 p-2 flex-1 min-h-0">
                <div className="flex-1" ref={footageRef}></div>
                <div className="basis-80" ref={mapRef}></div>
            </div>
            <div ref={timelineRef} />
        </div>
    )
}

interface ColorFinderFootageContainerProps {
    colorFilter: ColorFilter
    flight: ImageColorFinderFlight | VideoColorFinderFlight
    numFrames: number
}

const lerp = (p1: [number, number], p2: [number, number], t: number): [number, number] => {
    const lng = p1[0] + (p2[0] - p1[0]) * t
    const lat = p1[1] + (p2[1] - p1[1]) * t
    return [lng, lat]
}

const ColorFinderFootageContainer = forwardRef<MediaControls, ColorFinderFootageContainerProps>(
    ({ colorFilter, flight, numFrames }, ref) => {
        const dispatch = useAppDispatch()

        const isPointTool = useAppSelector(selectColorFinderTool) === "point"
        const iconTemplate = useAppSelector(selectIconTemplate)
        const view = useAppSelector(selectColorFinderView)
        const userId = useAppSelector(selectUserID)
        const anomalyEnabled = useAppSelector(selectColorFinderAnomalyEnabled)
        const sourceEnabled = useAppSelector(selectColorFinderSourceEnabled)

        const iconGhostRef = useRef<HTMLDivElement>(null)

        const { data: metadata } = useColorFinderMetadata(flight)

        const [isMouseDown, setIsMouseDown] = useState(false)
        const handleFootageAreaMouseDown = () => {
            if (isPointTool) return
            setIsMouseDown(true)
        }
        const handleFootageAreaMouseUp = () => {
            if (isPointTool) return
            setIsMouseDown(false)
        }

        const handleFootageClick: FootageClickHandler = async ({ frame, percentX, percentY }) => {
            if (!isPointTool || !userId) return

            const frameData = metadata?.[frame]
            if (!frameData) return

            const bbox = await getVideoFrameBboxAsync(frameData)
            if (!bbox || bbox.length < 4) return

            const topLeft = bbox[0] as [number, number]
            const topRight = bbox[1] as [number, number]
            const bottomRight = bbox[2] as [number, number]
            const bottomLeft = bbox[3] as [number, number]

            const topMidpoint = lerp(topLeft, topRight, percentX)
            const bottomMidpoint = lerp(bottomLeft, bottomRight, percentX)
            const clickLngLat = lerp(topMidpoint, bottomMidpoint, percentY)

            dispatch(
                createIcon({
                    data: {
                        latitude: clickLngLat[1],
                        longitude: clickLngLat[0],
                        name: iconTemplate.name,
                        url: iconTemplate.url,
                        createdBy: { userId },
                        color: iconTemplate.color,
                        targetPayload: JSON.stringify({
                            target: "cf-frame",
                            targetId: flight.id,
                            frame,
                        } satisfies ColorFinderIconTargetPayload),
                    },
                })
            )
            if (iconTemplate.url.startsWith("number")) {
                dispatch(incrementNumberStartAt())
            }
            dispatch(setColorFinderTool(""))
        }
        const hideAnomaly = view === "fullscreen" || !anomalyEnabled
        const hideSource = !sourceEnabled

        const anomalySensitivity = useAppSelector(selectColorFinderAnomalySensitivity)
        const anomalyOpacity = useAppSelector(selectColorFinderAnomalyOpacity)

        return (
            <div
                className={classNames(
                    "h-full border-2 border-transparent group relative",
                    isPointTool && "border-blue-500"
                )}
                onMouseMoveCapture={(e) => {
                    if (!iconGhostRef.current) return

                    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
                    const iconRect = iconGhostRef.current.getBoundingClientRect()

                    const x = e.clientX - rect.left - iconRect.width / 2
                    const y = e.clientY - rect.top - iconRect.height / 2

                    iconGhostRef.current.style.transform = `translate(${x}px, ${y}px)`
                }}
            >
                {isPointTool && (
                    <div
                        className="absolute invisible pointer-events-none group-hover:visible z-10"
                        ref={iconGhostRef}
                    >
                        <svg
                            style={{
                                fill:
                                    iconTemplate.pack === IconPackOption.Shape ||
                                    iconTemplate.pack === IconPackOption.Number
                                        ? decodeNumberToColor(iconTemplate.color) ||
                                          defaultIconColor
                                        : undefined,
                                stroke:
                                    iconTemplate.pack === IconPackOption.Number
                                        ? decodeNumberToColor(iconTemplate.outline) ||
                                          defaultIconColor
                                        : undefined,
                                strokeWidth:
                                    iconTemplate.pack === IconPackOption.Number
                                        ? iconTemplate.width === LineWidth.thick
                                            ? 1
                                            : iconTemplate.width === LineWidth.normal
                                              ? 0.5
                                              : 0
                                        : 0,
                            }}
                            data-src={getIconUrl(iconTemplate.url)}
                            height="1.5rem"
                            width="1.5rem"
                        />
                    </div>
                )}
                <ColorFinderFootageDisplay
                    colorFilter={isMouseDown ? new Set() : colorFilter}
                    ref={ref}
                    flight={flight}
                    numFrames={numFrames}
                    onFootageClick={handleFootageClick}
                    onFootageAreaMouseDown={handleFootageAreaMouseDown}
                    onFootageAreaMouseUp={handleFootageAreaMouseUp}
                    hideAnomaly={hideAnomaly}
                    anomalyOpacity={isMouseDown ? 0 : anomalyOpacity}
                    anomalySensitivity={anomalySensitivity}
                    hideSource={hideSource}
                />
            </div>
        )
    }
)

ColorFinderFootageContainer.displayName = "ColorFinderFootageDisplayContainer"

interface ColorFinderRightPanelProps {
    getMediaControls: GetMediaControls
    tiler: ColorFinderTiler
    flight: ImageColorFinderFlight | VideoColorFinderFlight
    comments: Comment<"cf-frame">[]
}

const ColorFinderRightPanel: FunctionComponent<ColorFinderRightPanelProps> = ({
    getMediaControls,
    flight,
    tiler,
    comments,
}) => {
    const icons = useAppSelector(selectIconsByTargetId(flight.id))

    const [currentCommentId, setCurrentCommentId] = useState<string>()
    const [isOpen, setIsOpen] = useState(true)

    const currentComment = comments.find((c) => c.id === currentCommentId)

    if (!isOpen)
        return (
            <div className="shrink-0 -mx-1">
                <div className="absolute -translate-x-full pl-4 pr-5 py-2">
                    <PanelOpenCloseButton isOpen={isOpen} onClick={() => setIsOpen(true)}>
                        <Icons.SmallIcon Icon={Icons.KeyboardDoubleArrowLeft} />
                    </PanelOpenCloseButton>
                </div>
            </div>
        )

    return (
        <div className="border border-white-200 rounded-lg h-full divide-y divide-white-200 flex flex-col w-[300px]">
            {!currentComment && (
                <div className="px-4 py-2 flex gap-3 items-center justify-between">
                    <div className="flex items-center gap-2">
                        <small>Activity</small>
                        <Popup
                            position="bottom-left"
                            content={
                                <Popup.PopupModal>
                                    <FeedFilters
                                        numComments={comments.length}
                                        numPoints={icons.length}
                                    />
                                </Popup.PopupModal>
                            }
                        >
                            <IconButton className="text-gray-100">
                                <Icons.SmallIcon Icon={Icons.FilterList} />
                            </IconButton>
                        </Popup>
                    </div>
                    <IconButton onClick={() => setIsOpen(false)} className="text-gray-500">
                        <Icons.SmallIcon Icon={Icons.KeyboardDoubleArrowRight} />
                    </IconButton>
                </div>
            )}
            <div className="flex flex-col flex-1">
                <div className="flex-1">
                    {currentComment ? (
                        <ColorFinderCommentRepliesFeed
                            flight={flight}
                            tiler={tiler}
                            onBackClick={() => setCurrentCommentId(undefined)}
                            getMediaControls={getMediaControls}
                            comment={currentComment}
                        />
                    ) : (
                        <ColorFinderFeed
                            tiler={tiler}
                            flight={flight}
                            getMediaControls={getMediaControls}
                            comments={comments}
                            icons={icons}
                            onCommentReplyClick={(comment) => setCurrentCommentId(comment.id)}
                        />
                    )}
                </div>

                <div className="p-4">
                    <ColorFinderCommentInput
                        targetComment={currentComment}
                        flight={flight}
                        getMediaControls={getMediaControls}
                    />
                </div>
            </div>
        </div>
    )
}

const FeedFilters = ({ numComments, numPoints }: { numComments: number; numPoints: number }) => {
    const dispatch = useAppDispatch()
    const filters = useAppSelector(selectColorFinderFeedFilters)
    return (
        <div className="flex flex-col gap-2">
            <small className="text-gray-300 select-none"> FILTER BY ACTIVITY</small>
            <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                    <div
                        onClick={() =>
                            dispatch(setColorFinderFeedFilters({ comments: !filters.comments }))
                        }
                        className=" flex gap-2 items-center cursor-pointer select-none"
                    >
                        <Checkbox onClick={() => {}} isChecked={filters.comments} />
                        <small>Comments</small>
                    </div>
                    <small>{numComments}</small>
                </div>

                <div className="flex gap-2 items-center">
                    <div
                        onClick={() =>
                            dispatch(setColorFinderFeedFilters({ points: !filters.points }))
                        }
                        className=" flex gap-2 items-center cursor-pointer select-none"
                    >
                        <Checkbox isChecked={filters.points} onClick={() => {}} />
                        <small>Points</small>
                    </div>
                    <small>{numPoints}</small>
                </div>
            </div>
        </div>
    )
}

const OpenCloseStyles = {
    light: "text-gray-400 hover:text-gray-0 active:text-gray-300 ",
    dark: "!text-gray-500 hover:!text-gray-900 active:!text-gray-700 ",
}

const stroke =
    "stroke-gray-600 stroke-1 text-white-1000 hover:stroke-gray-0 active:stroke-0 active:text-gray-300"

export const PanelOpenCloseButton = ({
    children,
    onClick,
    isOpen,
}: PropsWithChildren & { onClick: () => void; isOpen: boolean }) => {
    const { isLight } = useMapBackground()
    const view = useAppSelector(selectColorFinderView)

    const key = isOpen ? "dark" : view === "default" ? "light" : isLight ? "dark" : "light"

    const showStroke = key === "light" && !isOpen && !isLight && view === "flipped"

    return (
        <IconButton
            className={classNames(OpenCloseStyles[key], showStroke && stroke)}
            style={{ paintOrder: "stroke fill" }}
            onClick={onClick}
        >
            {children}
        </IconButton>
    )
}

export { ColorFinderPanel }
